import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CacheProvider } from './config/CacheContext';
import { RepresentativeProvider } from './config/RepresentativeContext';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import {
  AuthPage,
  AdminLoginPage,
  HomePage,
  ProfilePage,
  PollsPage,
  MyPollsPage,
  PublicPollPage,
  SnapshotPollPage,
  TransactionsPage,
  PaymentSuccessPage,
  VerifyEmailPage,
  ResetPasswordPage,
  ForgotPasswordPage,
  AccountSettingsPage,
  RepresentativesPage,
  AdminDashboard,
  AdminPolls,
  AdminTags,
  AdminPollResponses,
  AdminFlaggedPolls,
  AdminCustomPolls,
  AdminUsers,
  AdminOffices
} from './pages';
import AdminProfilePage from './pages/AdminProfilePage';
import AdminPollRatings from './pages/AdminPollRatings';
import AdminPollPricing from './pages/AdminPollPricing';
import AdminAssessmentPricing from './pages/AdminAssessmentPricing';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminApiUsage from './pages/AdminApiUsage';
import AdminVerifications from './pages/AdminVerifications';
import AdminFlaggedReps from './pages/AdminFlaggedReps';
import AdminPromoCodes from './pages/AdminPromoCodes';
import AdminRedisHealth from './pages/AdminRedisHealth';
import AdminRSSHealth from './pages/AdminRSSHealth';
import AdminRevenueAnalytics from './pages/AdminRevenueAnalytics';
import CompleteProfilePage from './pages/CompleteProfilePage';
import GoogleAuthCallback from './components/GoogleAuthCallback';
import NotificationsPage from './pages/NotificationsPage';
import PollDetailPage from './pages/PollDetailPage';
import RepsPage from './pages/RepsPage';
import LandingPage from './pages/LandingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import HelpSupportPage from './pages/HelpSupportPage';
import CandidacyReportPage from './pages/CandidacyReportPage';
import FavoritesPage from './pages/FavoritesPage';
import VerifyIdentityPage from './pages/VerifyIdentityPage';
import BillsPage from './pages/BillsPage';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Redirect to complete profile if needed (new Google users)
  if (user.needsProfileSetup && !requireAdmin) {
    return <Navigate to="/complete-profile" />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/polls" />;
  }

  return children;
}

// US-Only Protected Route Component
function USOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if user is from the United States
  const isUSUser = user.country === 'US' || user.country === 'United States' || user.country === 'USA';
  
  if (!isUSUser) {
    return <Navigate to="/home" />;
  }

  return children;
}

// Main App Component
function AppRoutes() {
  return (
    <Routes>
      {/* Google OAuth callback route */}
      <Route path="/auth/google" element={<GoogleAuthCallback />} />

      {/* Public routes without NavBar */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/complete-profile" element={<CompleteProfilePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/admin" element={<AdminLoginPage />} />
    <Route path="/admin/login" element={<AdminLoginPage />} />
    <Route path="/poll/:pollId" element={<PublicPollPage />} />
    <Route path="/poll/custom/:pollId" element={<PublicPollPage isCustomPoll={true} />} />
    <Route path="/poll/snapshot/:snapshotId" element={<SnapshotPollPage />} />
    <Route path="/verify-email" element={<VerifyEmailPage />} />
    <Route path="/verify/:token" element={<VerifyIdentityPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    
    {/* Protected user routes with persistent NavBar */}
    <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
      <Route path="/home" element={<HomePage />} />
      <Route path="/polls" element={<PollsPage />} />
      <Route path="/polls/:pollId" element={<PollDetailPage />} />
      <Route path="/reps" element={<USOnlyRoute><RepsPage /></USOnlyRoute>} />
      <Route path="/offices" element={<USOnlyRoute><RepsPage /></USOnlyRoute>} />
      <Route path="/representatives" element={<USOnlyRoute><RepsPage /></USOnlyRoute>} />
      <Route path="/representatives/:profileId" element={<USOnlyRoute><RepresentativesPage /></USOnlyRoute>} />
      <Route path="/my-polls" element={<MyPollsPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/account-settings" element={<AccountSettingsPage />} />
      <Route path="/transactions" element={<TransactionsPage />} />
      <Route path="/candidacy-report/:reportId" element={<CandidacyReportPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/help" element={<HelpSupportPage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/favorites" element={<FavoritesPage />} />
      <Route path="/bills" element={<BillsPage />} />
    </Route>
    
    {/* Admin routes with persistent AdminNavBar */}
    <Route element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/polls" element={<AdminPolls />} />
      <Route path="/admin/polls/:pollId/responses" element={<AdminPollResponses />} />
      <Route path="/admin/tags" element={<AdminTags />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/flagged-polls" element={<AdminFlaggedPolls />} />
      <Route path="/admin/custom-polls" element={<AdminCustomPolls />} />
      <Route path="/admin/offices" element={<AdminOffices />} />
      <Route path="/admin/poll-ratings" element={<AdminPollRatings />} />
      <Route path="/admin/poll-pricing" element={<AdminPollPricing />} />
      <Route path="/admin/assessment-pricing" element={<AdminAssessmentPricing />} />
      <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
      <Route path="/admin/api-usage" element={<AdminApiUsage />} />
      <Route path="/admin/verifications" element={<AdminVerifications />} />
      <Route path="/admin/flagged-reps" element={<AdminFlaggedReps />} />
      <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
      <Route path="/admin/redis-health" element={<AdminRedisHealth />} />
      <Route path="/admin/rss-health" element={<AdminRSSHealth />} />
      <Route path="/admin/revenue" element={<AdminRevenueAnalytics />} />
      <Route path="/admin/profile" element={<AdminProfilePage />} />
    </Route>
  </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <CacheProvider>
            <RepresentativeProvider>
              <AppRoutes />
            </RepresentativeProvider>
          </CacheProvider>
        </AuthProvider>
      </Router>
      <Analytics />
    </HelmetProvider>
  );
}

export default App;
